// §Phase 5 — Creator Asset Library panel. Lives in App Detail (owner side), NEVER in a customer
// app. Lists / uploads / deletes assets for an app the creator OWNS, over the REAL deployed upload
// server: the file bytes go through POST /v1/creator/assets (magic-byte MIME sniff, EXIF strip,
// size caps), then the result is cataloged under the app via POST /v1/apps/:appId/owner/assets.
// Listing is GET /v1/apps/:appId/owner/assets; delete is a soft-delete. Honest loading / empty /
// error / unavailable states; no fabricated rows. service-role stays server-side (never here).
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Images, Upload, Trash2, RotateCw, Copy, Check, FileText, Film, Music, File as FileIcon } from 'lucide-preact';
import { t } from '../lib/i18n';

interface Asset {
  id?: string;
  assetId: string;
  kind: 'image' | 'document' | 'audio' | 'video' | 'other';
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  publicUrl: string | null;
  status: string;
  createdAt?: number;
}

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: Asset[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' };

const KIND_ICON: Record<Asset['kind'], typeof Images> = {
  image: Images, document: FileText, video: Film, audio: Music, other: FileIcon,
};

function sizeLabel(n: number): string {
  if (!n || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AssetLibraryPanel({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' });
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState('');
  const [copied, setCopied] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchAssets = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/assets`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: Asset[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  async function onPickFile(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || busy) return;
    setBusy(true);
    setActionErr('');
    try {
      // 1) upload the bytes through the hardened, deployed creator upload server.
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch('/v1/creator/assets', { method: 'POST', credentials: 'include', body: fd });
      if (!up.ok) {
        setActionErr(up.status === 415 ? t('mgmt.as.err415')
          : up.status === 413 ? t('mgmt.as.err413')
          : up.status === 429 ? t('mgmt.as.err429')
          : up.status === 503 ? t('mgmt.as.err503')
          : t('mgmt.as.errUpload'));
        return;
      }
      const { asset } = (await up.json()) as { asset?: { key: string; kind: string; type: string; bytes: number; url?: string } };
      if (!asset?.key) { setActionErr(t('mgmt.as.errNoRef')); return; }
      // 2) catalog it under this app (owner-scoped).
      const scope = asset.kind === 'image' ? 'public_app' : 'creator';
      const reg = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/assets`, {
        method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mimeType: asset.type, sizeBytes: asset.bytes, storageKey: asset.key, publicUrl: asset.url ?? null, scope }),
      });
      if (!reg.ok) { setActionErr(t('mgmt.as.errCatalog')); return; }
      fetchAssets();
    } catch {
      setActionErr(t('mgmt.as.errConnUp'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove(a: Asset): Promise<void> {
    if (busy) return;
    setBusy(true);
    setActionErr('');
    try {
      const r = await fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/assets/${encodeURIComponent(a.assetId)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) {
        setLoad((cur) => (cur.s === 'ready' ? { s: 'ready', items: cur.items.filter((x) => x.assetId !== a.assetId) } : cur));
      } else {
        setActionErr(r.status === 404 ? t('mgmt.as.err404') : t('mgmt.as.errRemove'));
      }
    } catch {
      setActionErr(t('mgmt.errConn'));
    } finally {
      setBusy(false);
    }
  }

  function copyRef(a: Asset): void {
    const ref = a.publicUrl || a.storageKey;
    try {
      void navigator.clipboard?.writeText(ref);
      setCopied(a.assetId);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* clipboard blocked — non-fatal */ }
  }

  return (
    <section aria-label={t('mgmt.as.title')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Images size={15} /> {t('mgmt.as.title')}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <span style={{ display: 'inline-flex', gap: 7 }}>
          <button type="button" className="pf-btn pf-btn--sm pf-btn--soft" disabled={busy} onClick={() => fileRef.current?.click()}>
            <Upload size={13} /> {busy ? t('mgmt.as.uploading') : t('mgmt.as.upload')}
          </button>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchAssets} disabled={load.s === 'loading'}>
            <RotateCw size={13} /> {t('mgmt.refresh')}
          </button>
        </span>
      </div>
      <input ref={fileRef} type="file" hidden onChange={(e) => void onPickFile(e)} />

      {actionErr && <p style={{ fontSize: 12.5, color: 'var(--color-danger)', margin: 0 }}>{actionErr}</p>}

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} aria-hidden="true">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 56, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.as.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.as.unavailableS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.as.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t('mgmt.as.errAuth') : t('mgmt.as.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchAssets}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {/* empty state — EN copy 'No assets yet' lives in i18n as mgmt.as.emptyT */}
      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><Images size={22} /></span>
          <p className="pf-empty__title">{t('mgmt.as.emptyT')}</p>
          <p className="pf-empty__lede">{t('mgmt.as.emptyS')}</p>
        </div>
      )}

      {load.s === 'ready' && load.items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {load.items.map((a) => {
            const Icon = KIND_ICON[a.kind] ?? FileIcon;
            return (
              <div key={a.assetId} style={cardStyle}>
                {a.kind === 'image' && a.publicUrl
                  ? <img src={a.publicUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  : <span className="pf-bubble" aria-hidden="true" style={{ width: 40, height: 40, flexShrink: 0 }}><Icon size={18} /></span>}
                <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.storageKey.split('/').pop()}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{a.kind} · {a.mimeType} · {sizeLabel(a.sizeBytes)}</span>
                </div>
                <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
                  <button type="button" className="pf-btn pf-btn--sm" onClick={() => copyRef(a)}>
                    {copied === a.assetId ? <><Check size={13} /> {t('mgmt.as.copied')}</> : <><Copy size={13} /> {t('mgmt.as.copyRef')}</>}
                  </button>
                  <button type="button" className="pf-btn pf-btn--sm pf-btn--danger" disabled={busy} onClick={() => void remove(a)}>
                    <Trash2 size={13} /> {t('mgmt.as.remove')}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const cardStyle: JSX.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: 12,
  background: 'var(--bg-el)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
};
